// src/webideal-recruit/Layout.jsx
//
// WebIdeal Recruit · application shell, blueprint design language.

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import {
  LayoutDashboard,
  Briefcase,
  Users,
  Calendar,
  Search,
  Settings,
  Menu,
  X,
  LogOut,
  Bell,
  Plus,
  ChevronDown,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { colors as DS, TechLabel } from "../design-system";

const ACCENT = DS.product.recruit;

/* ROUTER */
function createRecruitUrl(pageName) {
  switch (pageName) {
    case "Dashboard":        return "/recruit/dashboard";
    case "Jobs":             return "/recruit/jobs";
    case "CreateJob":        return "/recruit/create-job";
    case "Candidates":       return "/recruit/candidates";
    case "CandidateDetail":  return "/recruit/candidate-detail";
    case "Pipeline":         return "/recruit/pipeline";
    case "Interviews":       return "/recruit/interviews";
    case "Settings":         return "/recruit/settings";
    case "JobDetail":        return "/recruit/job-detail";
    default:                 return "/recruit/dashboard";
  }
}
export { createRecruitUrl };

const navigation = [
  { idx: "01", name: "Dashboard",  href: "Dashboard",  icon: LayoutDashboard },
  { idx: "02", name: "Jobs",       href: "Jobs",       icon: Briefcase       },
  { idx: "03", name: "Candidates", href: "Candidates", icon: Users           },
  { idx: "04", name: "Pipeline",   href: "Pipeline",   icon: Search          },
  { idx: "05", name: "Interviews", href: "Interviews", icon: Calendar        },
  { idx: "06", name: "Settings",   href: "Settings",   icon: Settings        },
];

export default function RecruitLayout({ children, currentPageName }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/recruit/auth", { replace: true });
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: DS.paper,
        color: DS.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HEADER, forest dark with mint accents */}
      <header
        className="sticky top-0 z-50 border-b"
        style={{
          background: DS.forest,
          color: DS.paper,
          borderColor: "rgba(158,255,191,0.18)",
        }}
      >
        <div className="px-4 lg:px-6">
          <div className="flex items-center justify-between h-14">
            {/* LOGO + module tag */}
            <Link
              to={createRecruitUrl("Dashboard")}
              className="flex items-center gap-3 group"
            >
              <img
                src="/logo 2.jpg"
                className="w-10 h-10 object-contain bg-white p-1"
                style={{ border: `1px solid rgba(158,255,191,0.3)` }}
                alt="WebIdeal Logo"
              />
              <div>
                <span className="text-lg font-extrabold tracking-tight">WebIdeal</span>
                <TechLabel style={{ color: ACCENT }}>module · recruit</TechLabel>
              </div>
            </Link>

            {/* DESKTOP NAV */}
            <nav
              className="hidden lg:flex items-center gap-px"
              style={{ background: "rgba(158,255,191,0.18)" }}
            >
              {navigation.map((item) => {
                const isActive = currentPageName === item.href;
                return (
                  <Link
                    key={item.name}
                    to={createRecruitUrl(item.href)}
                    className="px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 inline-flex items-center gap-2"
                    style={{
                      background: isActive ? ACCENT : DS.forest,
                      color: isActive ? DS.forest : "rgba(247,247,245,0.85)",
                    }}
                  >
                    <span
                      className="text-[10px] font-bold tabular-nums"
                      style={{
                        letterSpacing: "0.16em",
                        color: isActive ? DS.forest : "rgba(247,247,245,0.5)",
                      }}
                    >
                      {item.idx}
                    </span>
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* RIGHT */}
            <div className="flex items-center gap-2">
              <Link to={createRecruitUrl("CreateJob")} className="hidden sm:block">
                <button
                  type="button"
                  className="ds-skeu-btn-mint cursor-pointer h-9 px-3.5 text-xs font-bold uppercase inline-flex items-center"
                  style={{ letterSpacing: "0.16em" }}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Post job
                </button>
              </Link>

              <button
                type="button"
                aria-label="Notifications"
                className="relative cursor-pointer w-9 h-9 flex items-center justify-center transition-colors hover:bg-[rgba(158,255,191,0.1)]"
                style={{ border: "1px solid rgba(158,255,191,0.25)" }}
              >
                <Bell className="w-4 h-4" />
                <span
                  className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: ACCENT }}
                />
              </button>

              {/* USER MENU */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 px-2 py-1.5 cursor-pointer transition-colors hover:bg-[rgba(158,255,191,0.1)]"
                      style={{ border: "1px solid rgba(158,255,191,0.25)" }}
                    >
                      <Avatar className="h-7 w-7 rounded-none">
                        {user.avatar_url && user.avatar_url.trim() !== "" ? (
                          <AvatarImage src={user.avatar_url} className="rounded-none" />
                        ) : null}
                        <AvatarFallback
                          className="text-[11px] font-extrabold rounded-none"
                          style={{ background: ACCENT, color: DS.forest }}
                        >
                          {(() => {
                            if (user.full_name) {
                              const parts = user.full_name.trim().split(" ");
                              return parts.length > 1
                                ? (parts[0][0] + parts[1][0]).toUpperCase()
                                : parts[0][0].toUpperCase();
                            }
                            if (user.email) return user.email[0].toUpperCase();
                            return "U";
                          })()}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown className="w-3 h-3 hidden sm:block" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-56 ds-glass"
                    style={{ borderRadius: 0, padding: 0 }}
                  >
                    <div
                      className="px-4 py-3 border-b"
                      style={{ borderColor: DS.hairline }}
                    >
                      <TechLabel style={{ color: ACCENT }}>user · session</TechLabel>
                      <p
                        className="text-sm font-bold mt-1 truncate"
                        style={{ color: DS.forest }}
                      >
                        {user.full_name || "User"}
                      </p>
                      <p className="text-xs truncate" style={{ color: DS.grid }}>
                        {user.email}
                      </p>
                    </div>
                    <DropdownMenuItem
                      asChild
                      className="cursor-pointer text-sm font-medium px-4 py-3"
                      style={{ color: DS.forest, borderRadius: 0 }}
                    >
                      <Link to={createRecruitUrl("Settings")} className="w-full">
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer text-sm font-medium px-4 py-3"
                      style={{ color: C_CORAL, borderRadius: 0 }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <button
                type="button"
                aria-label="Toggle menu"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-9 h-9 flex items-center justify-center"
                style={{ border: "1px solid rgba(158,255,191,0.25)" }}
              >
                {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* MOBILE NAV */}
        {mobileMenuOpen && (
          <nav
            className="lg:hidden border-t"
            style={{ borderColor: "rgba(158,255,191,0.18)" }}
          >
            <div className="grid grid-cols-2 gap-px" style={{ background: "rgba(158,255,191,0.18)" }}>
              {navigation.map((item) => {
                const isActive = currentPageName === item.href;
                return (
                  <Link
                    key={item.name}
                    to={createRecruitUrl(item.href)}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 text-sm font-semibold transition-colors flex items-center gap-2.5"
                    style={{
                      background: isActive ? ACCENT : DS.forest,
                      color: isActive ? DS.forest : "rgba(247,247,245,0.85)",
                    }}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      {/* MAIN */}
      <main className="p-4 lg:p-8">{children}</main>
    </div>
  );
}

// Coral for destructive actions (logout), matches the design system palette.
const C_CORAL = DS.coral;
